import { MutableRefObject } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { CoreBox, CoreTypography } from '@youscience/core';
import { TopTenMajor, areasDescriptionMap } from '@common-types';
import {
  sxStyles,
  StyledStatisticsWrap,
  StyledChartLegendWrap,
  StyledChartLegendGroup,
  StyledChartLegendItem,
} from './DegreesAwarded.styles';
import { PageSectionIds } from '@constants/schoolDetailsPage';
import { SectionLinkOffset } from '@pages/SchoolDetails/SchoolDetails.styles';

ChartJS.register(ArcElement, Tooltip);

const CHART_COLORS: { [k: number]: string } = {
  0: '#20C997',
  1: '#198754',
  2: '#FFC107',
  3: '#FD7E14',
  4: '#6610F2',
  5: '#C29FFA',
  6: '#D63384',
  7: '#EFADCE',
  8: '#0DCAF0',
  9: '#0B6EFD',
};

const PIE_CHART_OPTIONS = {
  responsive: true,
  plugins: {
    tooltip: {
      backgroundColor: '#212121',
      displayColors: false,
      callbacks: {
        // TODO check tooltip height
        label: (context: { label: string; formattedValue: string }) => {
          if (context.label.length > 22) {
            return `${context.formattedValue}% ${context.label.slice(0, 22)}...`;
          }

          return `${context.formattedValue}% ${context.label}`;
        },
      },
    },
  },
};

const getGraphicData = (data: TopTenMajor[]) => {
  if (!data || !Object.keys(data).length) {
    return null;
  }

  const { labels, values, valueColors } = Object.entries(data).reduce(
    (acc, [key, { percentage, cipCode = 0 }]) => ({
      labels: [...acc.labels, areasDescriptionMap.get(+cipCode)?.cipTitle || 'Other'],
      values: [...acc.values, percentage as number],
      valueColors: [...acc.valueColors, (CHART_COLORS as Record<string, unknown>)[key] as string],
    }),
    { labels: [] as string[], values: [] as number[], valueColors: [] as string[] },
  );

  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: valueColors,
        borderColor: valueColors,
        borderWidth: 0,
      },
    ],
  };
};

const MAX_LEGEND_IN_COLUMN = 5;

interface DegreesAwardedProps {
  scrollToRef: MutableRefObject<HTMLAnchorElement | null>;
  topTenMajors: TopTenMajor[];
}

export const DegreesAwarded = (props: DegreesAwardedProps) => {
  const { scrollToRef, topTenMajors } = props;

  if (!topTenMajors?.length) return null;

  const chartData = getGraphicData(topTenMajors);

  const isOneColumn = topTenMajors.length <= MAX_LEGEND_IN_COLUMN;

  const renderChartLegendGroup = (from: number, to?: number) => (
    <StyledChartLegendGroup isOneColumn={isOneColumn}>
      {topTenMajors.slice(from, to).map(({ percentage, cipCode = 0 }, index) => {
        if (!percentage) return null;

        return (
          <StyledChartLegendItem
            component="div"
            variant="body2"
            color={CHART_COLORS[index + from]}
            key={percentage + index}
          >
            {areasDescriptionMap.get(+cipCode)?.cipTitle || 'Other'}
          </StyledChartLegendItem>
        );
      })}
    </StyledChartLegendGroup>
  );

  return (
    <CoreBox sx={sxStyles.container}>
      <SectionLinkOffset id={PageSectionIds['degrees-awarded']} ref={scrollToRef} />

      <CoreTypography sx={sxStyles.sectionTitle} variant="h4" component="h3">
        Degrees awarded
      </CoreTypography>

      <CoreBox sx={sxStyles.degreesAwardedWrap}>
        <CoreTypography sx={sxStyles.sectionSubtitle} variant="h5" component="h4">
          Statistics
        </CoreTypography>

        {chartData?.labels && chartData.labels.length > 0 && (
          <StyledStatisticsWrap>
            <CoreBox sx={sxStyles.pieChart}>
              <Pie options={PIE_CHART_OPTIONS} data={chartData} />
            </CoreBox>

            <CoreBox sx={sxStyles.pieChartLegend}>
              <CoreTypography variant="overline" component="h5">
                Percentage of degrees awarded in the top areas of study
              </CoreTypography>

              <StyledChartLegendWrap>
                {renderChartLegendGroup(0, MAX_LEGEND_IN_COLUMN)}

                {renderChartLegendGroup(MAX_LEGEND_IN_COLUMN)}
              </StyledChartLegendWrap>
            </CoreBox>
          </StyledStatisticsWrap>
        )}
      </CoreBox>
    </CoreBox>
  );
};
